import { z } from 'zod';
import { schemas } from '../../generated/api-client';
import { TypedApiClient } from '../index';
import { apiPath } from '../path';
import { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from '../id-window';

// ====== Import schemas from generated code (ATL-450) ======
// Was hand-written with a TODO to switch to generated once api:sync was
// unblocked — now done. The generated schemas match transformTransfer 1:1
// (camelCase) and stay in sync via npm run api:sync.
const { TransferFirestoreResponse, CreateTransferDto, TransferItemDto } = schemas;

export const TransferItemSchema = TransferItemDto;
export const TransferResponseSchema = TransferFirestoreResponse;
export const CreateTransferSchema = CreateTransferDto;

// ====== Comparison Schema ======

export const TransferComparisonResultSchema = z.object({
  exists: z.boolean(),
  supabaseData: TransferResponseSchema.nullable(),
}).passthrough();

// ====== Type Exports ======

export type TransferResponse = z.infer<typeof TransferResponseSchema>;
export type CreateTransfer = z.infer<typeof CreateTransferSchema>;
export type TransferItem = z.infer<typeof TransferItemSchema>;
export type TransferComparisonResult = z.infer<typeof TransferComparisonResultSchema>;

/**
 * Create transfers API factory
 */
export function createTransfersApi(client: TypedApiClient) {
  return {
    /**
     * Get transfers with optional filters
     */
    getTransfers: (params?: {
      fromStoreId?: string;
      toStoreId?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
    }): Promise<TransferResponse[]> => {
      return client.get('/transfers', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(TransferResponseSchema),
      });
    },

    /**
     * ATL-1477: one snapshot window of `/transfers` — see `../id-window.ts`. Use it through
     * `readWholeList`, never directly: a single window is not the list.
     */
    getTransfersWindow: (
      params: {
      storeId?: string;
      startDate?: string;
      endDate?: string;
      } & IdWindowParams,
    ): Promise<IdWindowPage<z.infer<typeof TransferResponseSchema>>> => {
      return client.get('/transfers/window', {
        params: params as Record<string, unknown>,
        responseSchema: idWindowPageSchema(TransferResponseSchema),
      });
    },

    /**
     * Get a single transfer by ID
     */
    getTransferById: (id: string): Promise<TransferResponse> => {
      return client.get(apiPath`/transfers/${id}`, {
        responseSchema: TransferResponseSchema,
      });
    },

    /**
     * Create a new transfer
     */
    createTransfer: (data: CreateTransfer): Promise<TransferResponse> => {
      return client.post('/transfers', {
        data,
        requestSchema: CreateTransferSchema,
        responseSchema: TransferResponseSchema,
      });
    },

    /**
     * Delete a transfer
     */
    deleteTransfer: (id: string) => {
      return client.delete(apiPath`/transfers/${id}`, {
        responseSchema: z.object({ message: z.string() }),
      });
    },

    /**
     * Compare transfer with Firestore for dual-write verification
     */
    compareWithFirestore: (firestoreId: string): Promise<TransferComparisonResult> => {
      return client.get(apiPath`/transfers/compare/${firestoreId}`, {
        responseSchema: TransferComparisonResultSchema,
      });
    },
  };
}
